// ============================================================================
// mutuellesHelper.js — Source de vérité unique pour la résolution des mutuelles
// ============================================================================
// Refonte 2026-05 (audit Phase 2 F.2). Centralise :
//   • La table d'alias (consolide les anciennes MUT_ALIASES + MUTUELLE_ALIAS
//     dispersées dans App.jsx).
//   • La normalisation des noms (NFD + lowercase + trim).
//   • La résolution d'une chaîne libre vers un doc memberList canonique.
//
// REMPLACE PROGRESSIVEMENT :
//   • App.jsx:5305-5314 (MUT_ALIASES + resolveMut, scope AnnuaireAdminView)
//   • App.jsx:6494-6515 (MUTUELLE_ALIAS, scope ReunionInvitationModal)
//   • Tous les filtres `d.mutuelle === X` dispersés (à refactorer).
//
// Règle d'or : on travaille uniquement sur memberLists comme source de vérité.
// Si une valeur n'a pas de match dans memberLists (ni via alias), on retourne
// la valeur brute (pas de fallback silencieux qui masquerait l'incohérence).
// ============================================================================

// ─── Table d'alias canonique ────────────────────────────────────────────────
// Format : clé = nom alternatif normalisé (NFD + lowercase + trim)
//         valeur = nom canonique exact tel qu'il doit apparaître dans memberLists.name
// Note : les clés ET valeurs doivent être saisies avec leur orthographe complète
// (les valeurs sont des nom à matcher exactement dans memberLists).
//
// IMPORTANT : si tu ajoutes/renommes une mutuelle dans memberLists, mets à jour
// cette table pour inclure les anciennes orthographes en aliases.
export const MUTUELLE_ALIAS = {
  // SMH — bug historique : seed initial "SMH Hauts-de-France" remplacé par "SMH"
  "smh hauts-de-france": "SMH",
  "smh hauts de france": "SMH",
  "smh haut-de-france": "SMH",
  "s.m.h": "SMH",

  // UMGP (groupe, contient HEYME, MEP, SMEREP) — anciennes orthographes
  // 2026-05-25 : fusion des docs memberLists/heyme + /umgp en un seul doc "UMGP".
  // Le nom canonique est désormais "UMGP" (le doc memberLists s'appelle ainsi).
  "heyme": "UMGP",
  "heyme care": "UMGP",
  "heyme.care": "UMGP",
  "mep (umgp)": "UMGP",
  "smerep (umgp)": "UMGP",
  "mep": "UMGP",
  "smerep": "UMGP",

  // Amellis (parfois écrit Amellis Mutuelles)
  "amellis": "Amellis Mutuelles",
  "amellis mutuelle": "Amellis Mutuelles",
  "amelis": "Amellis Mutuelles",

  // Intériale (parfois écrit Groupe Interiale, sans accent)
  "interiale": "Intériale",
  "groupe interiale": "Intériale",
  "groupe intériale": "Intériale",

  // Intégrance (parfois sans accent)
  "integrance": "Intégrance",

  // Tutélaire (parfois sans accent)
  "tutelaire": "Tutélaire",

  // MGPS (anciennement MGAS)
  "mgas": "MGPS",
  "m.g.p.s": "MGPS",

  // Mutuelle de la Corse — DV-4 (2026-05-19) ajout depuis DatavizModule
  "mdc": "Mutuelle de la Corse",
  "mutuelle corse": "Mutuelle de la Corse",
  "m.d.c": "Mutuelle de la Corse",

  // Carel — DV-4 ajout depuis DatavizModule
  "carel": "Carel Mutuelle",

  // UMCL — DV-4 ajout (Mutlog = ancien nom)
  "mutlog": "UMCL",
  "u.m.c.l": "UMCL",

  // CMAM
  "c.m.a.m": "CMAM",

  // Mutami
  "mutami mutuelle": "Mutami",

  // Mutuale
  "mutuale mutuelle": "Mutuale",
};

// ─── Normalisation ──────────────────────────────────────────────────────────
// NFD + lowercase + trim : permet de matcher "SMH", "smh", "  smh ", "SMH"
// avec espaces décomposés Unicode, etc.
export function normalizeMutuelleName(s) {
  if (s == null) return "";
  return String(s).normalize("NFD").replace(/[̀-ͯ]/g, "").toLowerCase().trim();
}

// ─── Résolution principale ──────────────────────────────────────────────────
// Prend une valeur brute (delegate.mutuelle, signup form, etc.) et retourne :
//   • le doc memberList correspondant, OU
//   • null si rien ne match (ni direct, ni via alias)
//
// Algorithme :
//   1. Normalise la valeur.
//   2. Cherche un alias dans MUTUELLE_ALIAS → si trouvé, utilise le nom canonique.
//   3. Cherche dans memberLists par name normalisé.
//   4. Si rien : null.
export function resolveMutuelle(rawName, lists = []) {
  if (!rawName) return null;
  const norm = normalizeMutuelleName(rawName);
  if (!norm) return null;

  // Étape 1 : alias
  const aliasTarget = MUTUELLE_ALIAS[norm];
  const candidateNorm = aliasTarget ? normalizeMutuelleName(aliasTarget) : norm;

  // Étape 2 : lookup memberLists
  for (const l of lists) {
    if (normalizeMutuelleName(l.name) === candidateNorm) return l;
  }
  return null;
}

// ─── Helpers de lecture ────────────────────────────────────────────────────
// Retourne le nom canonique (depuis memberList si trouvé, sinon nom brut).
export function getMutuelleCanonicalName(rawName, lists = []) {
  const ml = resolveMutuelle(rawName, lists);
  return ml ? ml.name : (rawName || "");
}

// Retourne l'abréviation pour les pastilles. Fallback : initiales du nom.
export function getMutuelleAbreviation(rawName, lists = []) {
  const ml = resolveMutuelle(rawName, lists);
  if (ml?.abreviation) return ml.abreviation;
  // Fallback : initiales (max 4 lettres)
  const name = ml?.name || rawName || "";
  if (!name) return "?";
  const parts = name.split(/\s+/).filter(Boolean);
  if (parts.length === 1) return parts[0].slice(0, 4).toUpperCase();
  return parts.map(w => w.charAt(0)).join("").slice(0, 4).toUpperCase();
}

// Retourne la couleur principale (couleur1) ou un gris neutre.
export function getMutuelleCouleur(rawName, lists = [], fallback = "#6B7280") {
  const ml = resolveMutuelle(rawName, lists);
  return ml?.couleur1 || fallback;
}

// Retourne le logo (string base64 / URL) ou null.
export function getMutuelleLogo(rawName, lists = []) {
  const ml = resolveMutuelle(rawName, lists);
  return ml?.logo || null;
}

// ─── Comparaison robuste ────────────────────────────────────────────────────
// Remplace les filtres `d.mutuelle === X` par sameMutuelle(d.mutuelle, X, lists).
// Match sur :
//   • égalité après normalisation, OU
//   • même doc memberList résolu (via alias).
export function sameMutuelle(a, b, lists = []) {
  if (!a && !b) return true;
  if (!a || !b) return false;
  const na = normalizeMutuelleName(a);
  const nb = normalizeMutuelleName(b);
  if (na === nb) return true;
  const ra = resolveMutuelle(a, lists);
  const rb = resolveMutuelle(b, lists);
  if (ra && rb && ra.id === rb.id) return true;
  return false;
}

// ─── Diagnostic : valeurs orphelines ────────────────────────────────────────
// Retourne la liste des valeurs distinctes de `field` dans `delegates` qui ne
// trouvent aucun match dans memberLists (même via alias). Utilisé par le panel
// Diagnostic > Réconciliation mutuelles.
export function findOrphanMutuelles(delegates = [], lists = [], field = "mutuelle") {
  const map = new Map();
  for (const d of delegates) {
    if (d.actif === false || d.supprime) continue;
    const v = d[field];
    if (!v) continue;
    const norm = normalizeMutuelleName(v);
    if (!norm || ["enio", ""].includes(norm)) continue;
    if (resolveMutuelle(v, lists)) continue; // pas orphelin
    if (!map.has(norm)) map.set(norm, { rawValues: new Set(), members: [] });
    map.get(norm).rawValues.add(v);
    map.get(norm).members.push(d);
  }
  return Array.from(map.values()).map(o => ({
    normalized: [...o.rawValues][0] || "",
    rawValues: Array.from(o.rawValues),
    count: o.members.length,
    members: o.members,
  }));
}
